import '@/app.scss'
import { createApp } from 'vue'
import { init as apiInit, loadAuthInfo } from './helpers/api'
import { setGlobals, setSession, useSession } from './helpers/globals'

// Vuetify
import 'vuetify/styles'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { aliases, fa } from 'vuetify/iconsets/fa'
import { de } from 'vuetify/locale'

import App from './App.vue'
import colors from 'vuetify/util/colors'
import { storeCsrfToken } from './helpers/auth'
import { setupI18n, setupLocale } from './helpers/i18n'
import router from '@/router'
import { createModel } from '@kp/js-model'
import { Benutzer } from './models/Benutzer'

const apiRoot = import.meta.env.VITE_API_BASE
const appRoot = import.meta.env.VITE_BASE_URL

;(async () => {
    const tgTheme = {
        dark: false,
        colors: {
            primary: '#40a536',
            info: '#40a536',
            accent: '#40a536',
            action: '#40a536',
            subtleHint: '#ffec0e',
            error: colors.red.accent4,

            anchor: '#0645ad',
            accept: colors.green.darken1,
            decline: colors.grey.darken3,
            stop: colors.red.accent4,
        },
    }
    const shTheme = {
        dark: false,
        colors: {
            anchor: '#0645ad',
            primary: '#464646',
            info: '#FFDC00',
            accent: '#FFDC00',
            action: '#FFDC00',
            accept: colors.green.darken1,
            decline: colors.grey.darken3,
            subtleHint: '#FFDC00',
            stop: colors.red.accent4,
        },
    }

    const vuetify = createVuetify({
        components,
        directives,
        icons: {
            defaultSet: 'fa',
            aliases,
            sets: {
                fa,
            },
        },
        theme: {
            defaultTheme: 'tgTheme',
            themes: {
                tgTheme,
                shTheme,
            },
        },
        locale: {
            locale: 'de',
            messages: {
                de: {
                    ...de,
                },
            },
        },
        defaults: {
            VBtnToggle: {
                rounded: '0',
            },
            VCheckbox: {
                hideDetails: 'auto',
            },
            VTextField: {
                hideDetails: 'auto',
                rounded: '0',
                density: 'compact',
            },
            VNumberInput: {
                hideDetails: 'auto',
                rounded: '0',
                density: 'compact',
                controlVariant: 'stacked',
            },
            VCombobox: {
                hideDetails: 'auto',
                rounded: '0',
                density: 'compact',
            },
            VSelect: {
                hideDetails: 'auto',
                rounded: '0',
                density: 'compact',
            },
            VAutocomplete: {
                hideDetails: 'auto',
                rounded: '0',
                density: 'compact',
            },
        },
    })
    // Pre-loading API info before we start the app
    apiInit(apiRoot, appRoot)
    const authInfo = await loadAuthInfo()
    setGlobals({
        session: authInfo.sessionId,
        appVersion: authInfo.version,
        webroot: authInfo.webroot,
        loading: false,
        loadingCounter: 0,
        snackbarContent: '',
        redirectUrl: authInfo.redirectUrl,
        customer: authInfo.customer,
        config: authInfo.config,
    })
    let user = null
    if (authInfo?.apollonBenutzer) {
        user = createModel(Benutzer, authInfo.apollonBenutzer)
    }
    setSession({
        benutzer: user,
        csrfToken: authInfo?.csrfToken || null,
    })
    vuetify.theme.change(`${authInfo.customer}Theme`)
    storeCsrfToken(useSession().csrfToken)
    const locale = 'de'
    const result = await setupLocale(locale)
    const messages = result

    const app = createApp(App)
    app.use(vuetify)

    app.use(router)
    app.use(setupI18n(locale, messages))
    app.mount('#app')
})()
